/**
 * AOCTANT Search Drawer presentation enhancements.
 * Horizon continues to own dialog and predictive-search behavior.
 */
(function () {
  'use strict';

  const component = document.getElementById('search-modal');
  if (!component) return;

  const panel = component.querySelector('.aoctant-search-modal__dialog');
  const handle = component.querySelector('[data-aoctant-search-drag-handle]');
  const mobileQuery = window.matchMedia('(max-width: 639px)');

  component.addEventListener('dialog:open', () => {
    document.querySelectorAll('[data-aoctant-search-trigger] button[aria-controls="search-modal"]')
      .forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    panel?.classList.remove(
      'aoctant-search-swipe-active',
      'aoctant-search-swipe-resetting',
      'aoctant-search-swipe-settled'
    );
    panel?.style.removeProperty('transform');
    panel?.style.removeProperty('--aoctant-search-swipe-offset');
  });

  component.addEventListener('dialog:close', () => {
    document.querySelectorAll('[data-aoctant-search-trigger] button[aria-controls="search-modal"]')
      .forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    resetDrag();
  });

  if (!panel || !handle) return;

  const axisLockDistance = 8;
  const closeDistance = 80;
  const closeHeightRatio = 0.18;
  const resetDuration = 280;
  let gesture = null;
  let resetTimer = 0;
  let resetTransitionHandler = null;

  handle.addEventListener('pointerdown', (event) => {
    if (!mobileQuery.matches || gesture || !event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!panel.open || panel.classList.contains('dialog-closing')) return;

    clearPanelVisuals();
    gesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      travel: 0,
      direction: 'pending',
    };

    try {
      handle.setPointerCapture(event.pointerId);
    } catch {}
  });

  handle.addEventListener('pointermove', (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;
    const absX = Math.abs(deltaX);

    if (gesture.direction === 'pending') {
      if (Math.max(absX, Math.abs(deltaY)) < axisLockDistance) return;
      if (deltaY <= 0 || absX > deltaY) {
        cancelGesture();
        return;
      }
      gesture.direction = 'vertical';
      panel.classList.add('aoctant-search-swipe-active');
    }

    if (deltaY < 0 || absX > deltaY) {
      cancelGesture();
      return;
    }

    event.preventDefault();
    gesture.travel = deltaY;
    panel.style.transform = `translate3d(0, ${deltaY}px, 0)`;
  });

  handle.addEventListener('pointerup', finishDrag);
  handle.addEventListener('pointercancel', cancelGesture);
  window.addEventListener('blur', cancelGesture);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) cancelGesture();
  });
  mobileQuery.addEventListener('change', (event) => {
    if (!event.matches) cancelGesture();
  });

  function finishDrag(event) {
    if (!gesture || event.pointerId !== gesture.pointerId) return;

    const currentGesture = finishGesture();
    if (!currentGesture) return;

    const sheetHeight = panel.getBoundingClientRect().height;
    const shouldClose =
      currentGesture.direction === 'vertical' &&
      (currentGesture.travel >= closeDistance ||
        currentGesture.travel >= sheetHeight * closeHeightRatio);

    if (!shouldClose) {
      resetPanel(currentGesture);
      return;
    }

    clearResetTimer();
    panel.style.setProperty('--aoctant-search-swipe-offset', `${currentGesture.travel}px`);
    panel.classList.remove('aoctant-search-swipe-active', 'aoctant-search-swipe-resetting');
    panel.style.removeProperty('transform');

    if (typeof component.closeDialog === 'function') {
      component.closeDialog();
      window.setTimeout(() => {
        panel.style.removeProperty('--aoctant-search-swipe-offset');
      }, 700);
    } else {
      resetPanel(currentGesture);
    }
  }

  function resetDrag() {
    const currentGesture = finishGesture();
    if (currentGesture) releaseCapture(currentGesture);
    clearPanelVisuals();
  }

  function clearResetTimer() {
    if (!resetTimer) return;
    window.clearTimeout(resetTimer);
    resetTimer = 0;
  }

  function clearPanelVisuals() {
    clearResetTimer();
    if (resetTransitionHandler) {
      panel.removeEventListener('transitionend', resetTransitionHandler);
      resetTransitionHandler = null;
    }
    panel.classList.remove('aoctant-search-swipe-active', 'aoctant-search-swipe-resetting');
    panel.style.removeProperty('transform');
    panel.style.removeProperty('--aoctant-search-swipe-offset');
  }

  function releaseCapture(currentGesture) {
    if (!handle.hasPointerCapture(currentGesture.pointerId)) return;
    handle.releasePointerCapture(currentGesture.pointerId);
  }

  function finishGesture() {
    const currentGesture = gesture;
    gesture = null;
    if (!currentGesture) return null;

    try {
      releaseCapture(currentGesture);
    } catch {}

    return currentGesture;
  }

  function resetPanel(currentGesture) {
    if (currentGesture.travel <= 0) {
      clearPanelVisuals();
      return;
    }

    clearResetTimer();
    panel.classList.remove('aoctant-search-swipe-active');
    panel.classList.add('aoctant-search-swipe-resetting');
    panel.style.transform = `translate3d(0, ${currentGesture.travel}px, 0)`;

    requestAnimationFrame(() => {
      panel.style.transform = 'translate3d(0, 0, 0)';
    });

    const finishReset = () => {
      /* Keep the normal [open] entrance animation from restarting when the
         temporary reset animation is removed. The class is cleared on the
         next real dialog open so that lifecycle animation remains intact. */
      panel.classList.add('aoctant-search-swipe-settled');
      clearPanelVisuals();
    };
    resetTransitionHandler = (event) => {
      if (event.target === panel && event.propertyName === 'transform') finishReset();
    };
    panel.addEventListener('transitionend', resetTransitionHandler);
    resetTimer = window.setTimeout(finishReset, resetDuration + 80);
  }

  function cancelGesture() {
    const currentGesture = finishGesture();
    if (currentGesture) resetPanel(currentGesture);
  }
})();
